import React from 'react';
import { Alert, Button, DatePicker, Descriptions, Form, Input, Modal, Space, TimePicker, Upload, message } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs, { Dayjs } from 'dayjs';
import { useGetDocumentsByApplicationQuery, useGetDocumentsByRouteQuery, useUploadDocumentMutation, useCreateDocumentDispatchMutation } from '../../store/api/documentsApi';
import { useGetInvoicesListQuery, useGetRoutesExtendedQuery, useSendInvoiceToClientMutation } from '../../store/api/financeApi';
import { useCompleteControlTaskMutation } from '../../store/api/workspaceApi';
import { downloadDocument, resolveDocumentFilename, viewDocument } from '../../utils/documentDownload';
import type { DocumentFilenameSource } from '../../utils/documentDownload';
import type { ApplicationTaskModalAction } from '../../constants/applicationTaskModalActions';
import type { WorkspaceTask } from '../../store/api/workspaceApi';
import type { Document as AppDocument } from '../../types';
import CreateClientInvoiceModal from '../finance/CreateClientInvoiceModal';
import UploadDocumentModal from '../documents/UploadDocumentModal';
import FinanceRouteModal from '../../pages/finance/FinanceRouteModal';
import FinanceDetailsDrawer from '../../pages/finance/FinanceDetailsDrawer';
import RouteDocumentsModal from '../routes/RouteDocumentsModal';
import CreateRouteExpenseModal from '../finance/CreateRouteExpenseModal';
import { resolveWorkspaceTaskModal, type ResolvedWorkspaceModal } from '../workspace/workspaceTaskModalResolve';
import { ClosingPackageTaskModalHost } from '../../features/closingPackage/ui/modals';

export type { ApplicationTaskModalAction } from '../../constants/applicationTaskModalActions';

type Props = {
  /** Workspace: полная задача — единственный предпочтительный способ */
  task?: WorkspaceTask | null;
  /** Legacy: страница заявок с deep link */
  open?: boolean;
  applicationId?: number | null;
  action?: ApplicationTaskModalAction | null;
  invoiceId?: number | null;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

type DateTimeFields = {
  sendDate: Dayjs;
  sendTime: Dayjs;
  comment?: string;
};

function combineDateTime(values: DateTimeFields): string {
  return values.sendDate
    .hour(values.sendTime.hour())
    .minute(values.sendTime.minute())
    .second(0)
    .millisecond(0)
    .toISOString();
}

const ApplicationTaskModalHost: React.FC<Props> = ({
  task,
  open = false,
  applicationId: legacyApplicationId,
  action: legacyAction,
  invoiceId: legacyInvoiceId,
  onClose,
  onSuccess,
}) => {
  const effective = React.useMemo((): ResolvedWorkspaceModal | null => {
    if (task) {
      return resolveWorkspaceTaskModal(task);
    }
    if (open && legacyApplicationId && legacyAction) {
      return {
        type: 'application',
        applicationId: legacyApplicationId,
        action: legacyAction,
        invoiceId: legacyInvoiceId ?? null,
      };
    }
    return null;
  }, [task, open, legacyApplicationId, legacyAction, legacyInvoiceId]);

  const routeIdForFetch = React.useMemo(() => {
    if (!effective) return undefined;
    if (
      effective.type === 'route-upload' ||
      effective.type === 'route-task-confirm' ||
      effective.type === 'route-finance' ||
      effective.type === 'route-documents'
    ) {
      return effective.routeId;
    }
    return undefined;
  }, [effective]);

  const { data: routeExtendedData } = useGetRoutesExtendedQuery(
    { routeId: routeIdForFetch, limit: 1 },
    { skip: !routeIdForFetch }
  );
  const routeForModals = routeExtendedData?.routes?.[0] ?? null;

  const applicationId =
    effective?.type === 'application'
      ? effective.applicationId
      : effective?.type === 'application-finance-drawer'
        ? effective.applicationId
        : legacyApplicationId ?? null;
  const action = effective?.type === 'application' ? effective.action : legacyAction ?? null;
  const invoiceId = effective?.type === 'application' ? effective.invoiceId : legacyInvoiceId;

  const isApplicationModal = effective?.type === 'application';
  const modalOpen = Boolean(effective);

  const [form] = Form.useForm();
  const [invoiceForm] = Form.useForm();
  const [loadingFileList, setLoadingFileList] = React.useState<UploadFile[]>([]);
  const [activeDownloadId, setActiveDownloadId] = React.useState<number | null>(null);
  const [routeExpenseModalVisible, setRouteExpenseModalVisible] = React.useState(false);

  const [uploadDocument, { isLoading: uploadLoading }] = useUploadDocumentMutation();
  const [createDocumentDispatch, { isLoading: dispatchLoading }] = useCreateDocumentDispatchMutation();
  const [sendInvoiceToClient, { isLoading: sendInvoiceLoading }] = useSendInvoiceToClientMutation();
  const [completeControlTask, { isLoading: completeRouteTaskLoading }] = useCompleteControlTaskMutation();

  const emitSuccess = React.useCallback(() => Promise.resolve(onSuccess?.()), [onSuccess]);

  const { data: documents = [], refetch: refetchDocuments } = useGetDocumentsByApplicationQuery(applicationId ?? 0, {
    skip: !modalOpen || !applicationId || !isApplicationModal,
  });
  const { data: invoicesData, refetch: refetchInvoices } = useGetInvoicesListQuery(
    {
      page: 1,
      limit: 50,
      applicationId: applicationId ?? undefined,
      direction: 'INCOME',
      counterpartyType: 'CLIENT',
    },
    { skip: !modalOpen || !applicationId || !isApplicationModal }
  );
  const { data: routeDocuments = [], refetch: refetchRouteDocuments } = useGetDocumentsByRouteQuery(routeIdForFetch ?? 0, {
    skip: !modalOpen || !routeIdForFetch,
  });
  const { data: routeInvoicesData, refetch: refetchRouteInvoices } = useGetInvoicesListQuery(
    {
      page: 1,
      limit: 20,
      routeId: routeIdForFetch,
      direction: 'EXPENSE',
      counterpartyType: 'CARRIER',
    },
    { skip: !modalOpen || !routeIdForFetch }
  );

  const invoices = invoicesData?.invoices ?? [];
  const selectedInvoice =
    (invoiceId ? invoices.find((item) => item.id === invoiceId) : undefined) ||
    invoices.find((item) => String(item.status || '').toUpperCase() === 'CREATED') ||
    invoices[0];
  const latestUpdDocument = documents.find((item) => String(item.documentType || '').toUpperCase() === 'UPD_ACT') || null;
  const routeInvoices = routeInvoicesData?.invoices ?? [];
  const latestCarrierInvoiceDocument = routeDocuments.find((item: AppDocument) =>
    ['CARRIER_INVOICE', 'LOADING_INVOICE'].includes(String(item.documentType || '').toUpperCase())
  ) || null;
  const latestCarrierInvoice =
    routeInvoices.find((item) => item.fileDocumentId === latestCarrierInvoiceDocument?.id) ||
    routeInvoices[0] ||
    null;
  const latestRouteUpdDocument =
    routeDocuments.find((item: AppDocument) => String(item.documentType || '').toUpperCase() === 'UPD_ACT') || null;

  React.useEffect(() => {
    if (!modalOpen) {
      form.resetFields();
      invoiceForm.resetFields();
      setLoadingFileList([]);
      return;
    }

    const now = dayjs();
    form.setFieldsValue({ sendDate: now, sendTime: now });
    invoiceForm.setFieldsValue({ sendDate: now, sendTime: now });
  }, [form, invoiceForm, modalOpen]);

  const closeWithRefresh = async () => {
    try {
      await Promise.all([refetchDocuments(), refetchInvoices()]);
    } catch {}
    await emitSuccess();
    onClose();
  };

  const refetchActiveDocuments = async () => {
    const requests: Array<Promise<unknown>> = [];
    if (applicationId && isApplicationModal) {
      requests.push(refetchDocuments(), refetchInvoices());
    }
    if (routeIdForFetch) {
      requests.push(refetchRouteDocuments(), refetchRouteInvoices());
    }
    await Promise.all(requests);
  };

  const handleDownload = async (documentId: number, doc?: AppDocument | DocumentFilenameSource | null) => {
    try {
      setActiveDownloadId(documentId);
      await downloadDocument(documentId, doc ?? { id: documentId });
      await refetchActiveDocuments();
      message.success('Файл скачан');
    } catch (error) {
      console.error('Download failed', error);
      message.error('Не удалось скачать файл');
    } finally {
      setActiveDownloadId(null);
    }
  };

  const handleView = async (documentId: number) => {
    try {
      setActiveDownloadId(documentId);
      await viewDocument(documentId);
      await refetchActiveDocuments();
    } catch (error) {
      console.error('View failed', error);
      message.error('Не удалось открыть файл');
    } finally {
      setActiveDownloadId(null);
    }
  };

  const submitUploadAndDispatch = async (params: {
    documentType: string;
    stage: 'loading' | 'unloading' | 'other';
    taskType: string;
    includeDispatch: boolean;
    comment?: string;
    dispatchedAt?: string;
  }) => {
    if (!applicationId) return;
    const files = loadingFileList.map((file) => file.originFileObj).filter(Boolean) as File[];
    if (files.length === 0) {
      message.error('Выберите файл');
      return;
    }

    const uploadedDocuments = await uploadDocument({
      applicationId,
      documentType: params.documentType,
      stage: params.stage,
      fileType: 'file',
      files,
      comment: params.comment,
    }).unwrap();

    if (params.includeDispatch && params.dispatchedAt) {
      await createDocumentDispatch({
        applicationId,
        dispatchType: 'CLIENT',
        dispatchedAt: params.dispatchedAt,
        documentIds: uploadedDocuments.map((item) => item.id),
        comment: params.comment,
        taskType: params.taskType,
      }).unwrap();
    }

    await closeWithRefresh();
  };

  const renderInvoiceConfirm = () => {
    if (!selectedInvoice) {
      return <Alert type="warning" showIcon message="Не найден активный счет клиента для подтверждения отправки." />;
    }

    if (!selectedInvoice.fileDocumentId) {
      return <Alert type="error" showIcon message="У счета отсутствует прикрепленный файл." />;
    }

    const canConfirm = Boolean(selectedInvoice.fileDownloadedAt);

    return (
      <Modal
        title="Подтвердить отправку счета"
        open={modalOpen}
        onCancel={onClose}
        onOk={async () => {
          try {
            const values = await invoiceForm.validateFields();
            await sendInvoiceToClient({
              invoiceId: selectedInvoice.id,
              sentToClientAt: combineDateTime(values),
              comment: values.comment,
            }).unwrap();
            message.success('Отправка счета подтверждена');
            await closeWithRefresh();
          } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.data?.error || error?.message || 'Ошибка подтверждения отправки счета');
          }
        }}
        okText="Подтвердить отправку"
        cancelText="Отмена"
        okButtonProps={{ disabled: !canConfirm, loading: sendInvoiceLoading }}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type={canConfirm ? 'success' : 'info'}
            showIcon
            message={
              canConfirm
                ? `Файл счета уже скачан${selectedInvoice.fileDownloadedAt ? `: ${dayjs(selectedInvoice.fileDownloadedAt).format('DD.MM.YYYY HH:mm')}` : ''}`
                : 'Сначала скачайте файл счета, затем можно подтверждать отправку.'
            }
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              void handleDownload(Number(selectedInvoice.fileDocumentId), {
                id: Number(selectedInvoice.fileDocumentId),
                content: { originalName: `invoice-${selectedInvoice.number || selectedInvoice.id}.pdf` },
              })
            }
            loading={activeDownloadId === selectedInvoice.fileDocumentId}
          >
            Скачать счет
          </Button>
          <Form form={invoiceForm} layout="vertical">
            <Form.Item name="sendDate" label="Дата отправки" rules={[{ required: true, message: 'Укажите дату' }]}>
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item name="sendTime" label="Время отправки" rules={[{ required: true, message: 'Укажите время' }]}>
              <TimePicker style={{ width: '100%' }} format="HH:mm" />
            </Form.Item>
            <Form.Item name="comment" label="Комментарий">
              <Input.TextArea rows={3} placeholder="Комментарий к отправке" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    );
  };

  const renderFileUploadModal = (
    title: string,
    documentType: string,
    stage: 'loading' | 'unloading' | 'other',
    taskType: string,
    includeDispatch: boolean
  ) => (
    <Modal
      title={title}
      open={modalOpen}
      onCancel={onClose}
      onOk={async () => {
        try {
          const values = await form.validateFields();
          await submitUploadAndDispatch({
            documentType,
            stage,
            taskType,
            includeDispatch,
            comment: values.comment,
            dispatchedAt: includeDispatch ? combineDateTime(values) : undefined,
          });
          message.success('Действие успешно выполнено');
        } catch (error: any) {
          if (error?.errorFields) return;
          message.error(error?.data?.error || error?.message || 'Ошибка сохранения документов');
        }
      }}
      okText={includeDispatch ? 'Загрузить и подтвердить отправку' : 'Загрузить'}
      cancelText="Отмена"
      okButtonProps={{ loading: uploadLoading || dispatchLoading }}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Файлы" required>
          <Upload
            beforeUpload={() => false}
            multiple
            fileList={loadingFileList}
            onChange={({ fileList }) => setLoadingFileList(fileList)}
          >
            <Button icon={<UploadOutlined />}>Выбрать файлы</Button>
          </Upload>
        </Form.Item>
        {includeDispatch ? (
          <>
            <Form.Item name="sendDate" label="Дата отправки" rules={[{ required: true, message: 'Укажите дату' }]}>
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item name="sendTime" label="Время отправки" rules={[{ required: true, message: 'Укажите время' }]}>
              <TimePicker style={{ width: '100%' }} format="HH:mm" />
            </Form.Item>
          </>
        ) : null}
        <Form.Item name="comment" label="Комментарий">
          <Input.TextArea rows={3} placeholder="Комментарий" />
        </Form.Item>
      </Form>
    </Modal>
  );

  const renderUpdConfirm = () => {
    if (!latestUpdDocument) {
      return <Modal title="Подтвердить отправку УПД" open={modalOpen} onCancel={onClose} footer={null}><Alert type="warning" showIcon message="Сначала загрузите УПД." /></Modal>;
    }

    const canConfirm = Boolean(latestUpdDocument.lastDownloadedAt);

    return (
      <Modal
        title="Подтвердить отправку УПД"
        open={modalOpen}
        onCancel={onClose}
        onOk={async () => {
          try {
            const values = await form.validateFields();
            await createDocumentDispatch({
              applicationId: Number(applicationId),
              dispatchType: 'CLIENT',
              dispatchedAt: combineDateTime(values),
              documentIds: [latestUpdDocument.id],
              comment: values.comment,
              taskType: 'CONFIRM_UPD_SENT',
            }).unwrap();
            message.success('Отправка УПД подтверждена');
            await closeWithRefresh();
          } catch (error: any) {
            if (error?.errorFields) return;
            message.error(error?.data?.error || error?.message || 'Ошибка подтверждения отправки УПД');
          }
        }}
        okText="Подтвердить отправку"
        cancelText="Отмена"
        okButtonProps={{ disabled: !canConfirm, loading: dispatchLoading }}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type={canConfirm ? 'success' : 'info'}
            showIcon
            message={
              canConfirm
                ? `УПД уже скачан${latestUpdDocument.lastDownloadedAt ? `: ${dayjs(latestUpdDocument.lastDownloadedAt).format('DD.MM.YYYY HH:mm')}` : ''}`
                : 'Сначала скачайте УПД, затем можно подтверждать отправку.'
            }
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={() => void handleDownload(latestUpdDocument.id, latestUpdDocument)}
            loading={activeDownloadId === latestUpdDocument.id}
          >
            Скачать УПД
          </Button>
          <Form form={form} layout="vertical">
            <Form.Item name="sendDate" label="Дата отправки" rules={[{ required: true, message: 'Укажите дату' }]}>
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item name="sendTime" label="Время отправки" rules={[{ required: true, message: 'Укажите время' }]}>
              <TimePicker style={{ width: '100%' }} format="HH:mm" />
            </Form.Item>
            <Form.Item name="comment" label="Комментарий">
              <Input.TextArea rows={3} placeholder="Комментарий к отправке" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    );
  };

  if (effective?.type === 'closing-package') {
    return (
      <ClosingPackageTaskModalHost
        open={modalOpen}
        onClose={onClose}
        onSuccess={emitSuccess}
        applicationId={effective.applicationId}
        taskType={effective.taskType}
        modalKind={effective.modalKind}
      />
    );
  }

  if (effective?.type === 'application-finance-drawer') {
    return (
      <FinanceDetailsDrawer
        open
        mode="application"
        route={null}
        applicationId={effective.applicationId}
        focusObligationId={effective.obligationId ?? null}
        onClose={() => {
          void emitSuccess();
          onClose();
        }}
        onRefetchApplications={() => void emitSuccess()}
      />
    );
  }

  if (effective?.type === 'create-invoice') {
    return (
      <CreateClientInvoiceModal
        visible
        preselectedApplicationId={effective.applicationId}
        onCancel={onClose}
        onSuccess={async () => {
          await emitSuccess();
          onClose();
        }}
      />
    );
  }

  if (effective?.type === 'route-upload') {
    const uploadScope =
      effective.applicationId != null ||
      effective.groupId != null ||
      effective.routeStopId != null ||
      effective.taskScope
        ? {
            applicationId: effective.applicationId,
            groupId: effective.groupId,
            routeStopId: effective.routeStopId,
            taskScope: effective.taskScope,
          }
        : undefined;

    return (
      <UploadDocumentModal
        visible={modalOpen}
        onCancel={onClose}
        onSuccess={async () => {
          await emitSuccess();
          onClose();
        }}
        route={{ id: effective.routeId } as any}
        application={
          effective.applicationId != null ? ({ id: effective.applicationId } as any) : undefined
        }
        uploadScope={uploadScope}
        preset={effective.preset}
      />
    );
  }

  if (effective?.type === 'route-task-confirm') {
    const title =
      effective.taskType === 'CARRIER_INVOICE_CONFIRM'
        ? 'Подтвердить счёт перевозчика'
        : 'Подтвердить УПД';
    const successMessage =
      effective.taskType === 'CARRIER_INVOICE_CONFIRM'
        ? 'Счёт перевозчика подтверждён'
        : 'УПД подтверждён';
    const documentToShow = effective.taskType === 'CARRIER_INVOICE_CONFIRM'
      ? latestCarrierInvoiceDocument
      : latestRouteUpdDocument;
    const documentFileName = documentToShow ? resolveDocumentFilename(documentToShow) : '—';
    const invoiceAmount = latestCarrierInvoice?.amountWithVAT != null
      ? `${Number(latestCarrierInvoice.amountWithVAT).toLocaleString('ru-RU', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })} ₽`
      : '—';

    return (
      <>
      <Modal
        title={title}
        open={modalOpen}
        onCancel={onClose}
        onOk={async () => {
          try {
            await completeControlTask(effective.taskId).unwrap();
            message.success(successMessage);
            await emitSuccess();
            onClose();
          } catch (e: unknown) {
            const err = e as { data?: { error?: string }; message?: string };
            message.error(err?.data?.error || err?.message || 'Ошибка');
          }
        }}
        confirmLoading={completeRouteTaskLoading}
        okText="Подтвердить"
        cancelText="Отмена"
        okButtonProps={{ disabled: !documentToShow?.id }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space>
            <Button onClick={() => setRouteExpenseModalVisible(true)}>Добавить расходы</Button>
            <CancelBtn />
            <OkBtn />
          </Space>
        )}
        destroyOnClose
      >
        {!documentToShow ? (
          <Alert
            type="warning"
            showIcon
            message={
              effective.taskType === 'CARRIER_INVOICE_CONFIRM'
                ? 'Сначала загрузите счёт перевозчика.'
                : 'Сначала загрузите УПД.'
            }
          />
        ) : effective.taskType === 'CARRIER_INVOICE_CONFIRM' ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Номер счета">{latestCarrierInvoice?.number || '—'}</Descriptions.Item>
              <Descriptions.Item label="Дата счета">
                {latestCarrierInvoice?.invoiceDate ? dayjs(latestCarrierInvoice.invoiceDate).format('DD.MM.YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Сумма">{invoiceAmount}</Descriptions.Item>
              <Descriptions.Item label="Дата получения">
                {documentToShow.receivedDate ? dayjs(documentToShow.receivedDate).format('DD.MM.YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Файл">{documentFileName}</Descriptions.Item>
            </Descriptions>
            <Space>
              <Button
                onClick={() => documentToShow.id && void handleView(documentToShow.id)}
                loading={activeDownloadId === documentToShow.id}
              >
                Просмотр
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => documentToShow?.id && void handleDownload(documentToShow.id, documentToShow)}
                loading={activeDownloadId === documentToShow.id}
              >
                Скачать
              </Button>
            </Space>
          </Space>
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Документ">УПД</Descriptions.Item>
              <Descriptions.Item label="Дата получения">
                {documentToShow.receivedDate ? dayjs(documentToShow.receivedDate).format('DD.MM.YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Файл">{documentFileName}</Descriptions.Item>
            </Descriptions>
            <Space>
              <Button
                onClick={() => documentToShow.id && void handleView(documentToShow.id)}
                loading={activeDownloadId === documentToShow.id}
              >
                Просмотр
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => documentToShow?.id && void handleDownload(documentToShow.id, documentToShow)}
                loading={activeDownloadId === documentToShow.id}
              >
                Скачать
              </Button>
            </Space>
          </Space>
        )}
      </Modal>
      <CreateRouteExpenseModal
        visible={routeExpenseModalVisible}
        routeId={effective.routeId}
        onClose={() => setRouteExpenseModalVisible(false)}
        onSuccess={async () => {
          setRouteExpenseModalVisible(false);
          await emitSuccess();
        }}
      />
      </>
    );
  }

  if (effective?.type === 'route-finance') {
    if (!routeForModals) {
      return (
        <Modal open={modalOpen} onCancel={onClose} footer={null} title="Маршрут" destroyOnClose>
          <div style={{ padding: 24 }}>Загрузка данных маршрута…</div>
        </Modal>
      );
    }
    return (
      <FinanceRouteModal
        visible={modalOpen}
        route={routeForModals}
        onClose={onClose}
        onRefetch={() => void emitSuccess()}
      />
    );
  }

  if (effective?.type === 'route-documents') {
    if (!routeForModals) {
      return (
        <Modal open={modalOpen} onCancel={onClose} footer={null} title="Документы маршрута" destroyOnClose>
          <div style={{ padding: 24 }}>Загрузка…</div>
        </Modal>
      );
    }
    return (
      <RouteDocumentsModal
        visible={modalOpen}
        route={routeForModals}
        onClose={onClose}
        onChanged={() => void emitSuccess()}
      />
    );
  }

  if (!isApplicationModal || !applicationId || !action) {
    return null;
  }

  switch (action) {
    case 'confirm-invoice-sent':
      return renderInvoiceConfirm();
    case 'upload-loading-docs':
      return renderFileUploadModal('Загрузить документы погрузки', 'LOADING_TN', 'loading', 'UPLOAD_LOADING_DOCS', true);
    case 'upload-unloading-docs':
      return renderFileUploadModal('Загрузить документы выгрузки', 'UNLOADING_DOC', 'unloading', 'UPLOAD_UNLOADING_DOCS', true);
    case 'upload-upd':
      return renderFileUploadModal('Загрузить УПД', 'UPD_ACT', 'other', 'UPLOAD_UPD', false);
    case 'confirm-upd-sent':
      return renderUpdConfirm();
    default:
      return null;
  }
};

export default ApplicationTaskModalHost;
